<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";

import OmniTable from "@/components/omni/OmniTable.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import OmniButtonToggle from "@/components/omni/OmniButtonToggle.vue";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import PageHeader from "@/components/header/PageHeader.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useBaseView } from "@/composables/useBaseView";
import { useDateTime } from "@/composables/useDateTime";
import { useError } from "@/composables/useError";
import { useFilters } from "@/composables/useFilters";
import { API } from "@/api/api";
import { DialogSize } from "@/constants/dialog";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { ExpirationType } from "@/api/reports/dto/reports";
import { TruckStatus } from "@/constants/truck";
import type { ExpirationRowDto } from "@/api/reports/dto/reports";
import type { ExpirationTab, ExpirationTabCounts, ExpirationTabOption, ExpirationsFilter } from "@/types/expirations";

defineOptions({ name: "ExpirationsPage" });

const dbDateFormat = "YYYY-MM-DD";
const allTypes = "all";
// one of OmniTable's own per-page options, so the footer select stays in sync
const defaultPageSize = 25;

const { t } = useI18n();
const { hasAccess, setTitle } = useBaseView();
const { daysLeft } = useDateTime();
const { date: formatDate, mdash } = useFilters();
const { showAPIError } = useError();

const snackbarStore = useSnackbarStore();
const api = new API();

// the report starts on the next working day, so Friday through Sunday roll forward to Monday
const nextWorkingDay = () => {
  const today = dayjs();

  switch (today.day()) {
    case 5:
      return today.add(3, "day");
    case 6:
      return today.add(2, "day");
    default:
      return today.add(1, "day");
  }
};

// the report waits for the user to hit Apply before loading anything
const defaultFilter = (): ExpirationsFilter => ({
  dateTo: nextWorkingDay().format(dbDateFormat),
});

const headers = [
  { title: t("trucks.truck"), key: "truckNumber", sortable: false },
  { title: t("general.type"), key: "type", sortable: false },
  { title: t("trucks.expiration"), key: "expireDate", sortable: false },
  { title: t("drivers.driver"), key: "driverName", sortable: false },
  { title: t("drivers.owner"), key: "ownerName", sortable: false },
  { title: t("trucks.owner-company"), key: "ownerCompanyName", sortable: false },
  { title: "", key: "actions", sortable: false, align: "center" as const },
];

const expirationTypeLabels: Record<ExpirationType, string> = {
  [ExpirationType.licenceExpire]: t("trucks.expiration-licence"),
  [ExpirationType.driverLicenseExpire]: t("trucks.expiration-driver-license"),
  [ExpirationType.companyInsuranceExpire]: t("trucks.expiration-company-insurance"),
};

const typeOptions: ExpirationTabOption[] = [
  { value: allTypes, label: t("general.all") },
  { value: ExpirationType.licenceExpire, label: t("trucks.expiration-licence") },
  { value: ExpirationType.driverLicenseExpire, label: t("trucks.expiration-driver-license") },
  { value: ExpirationType.companyInsuranceExpire, label: t("trucks.expiration-company-insurance") },
];

const filter = ref<ExpirationsFilter>(defaultFilter());
const typeFilter = ref<ExpirationTab>(allTypes);
// tabs, counter and table stay hidden until the first search is applied
const searched = ref(false);
const page = ref(1);
const items = ref<ExpirationRowDto[]>([]);
const loading = ref(false);
const dialogVisible = ref(false);
const holding = ref(false);
const selectedRow = ref<ExpirationRowDto | null>(null);

// every expiration hangs off a truck, so the only action is putting that truck on hold
const isOnHold = (row: ExpirationRowDto) => row.truckStatus === TruckStatus.OnHold;

// a truck cannot leave the road mid-trip, so the backend refuses the status change
const isInRoute = (row: ExpirationRowDto) => row.truckStatus === TruckStatus.InRoute;

const canEditTruck = computed(() => hasAccess(DispatcherPermissionValue.TrucksEdit));
const canEditUser = computed(() => hasAccess(DispatcherPermissionValue.UsersEdit));
const canChangeTruckStatus = computed(() => hasAccess(DispatcherPermissionValue.TrucksChangeStatus));

const daysLeftText = (date: string) => {
  const days = daysLeft(date);

  if (days === 0) {
    return t("trucks.expiration-today");
  }

  if (days < 0) {
    const overdue = Math.abs(days);
    return overdue === 1
      ? t("trucks.expiration-overdue-day", { days: overdue })
      : t("trucks.expiration-overdue-days", { days: overdue });
  }

  return days === 1 ? t("trucks.expiration-in-day", { days }) : t("trucks.expiration-in-days", { days });
};

const daysLeftClass = (date: string) => {
  const days = daysLeft(date);

  if (days < 0) return "text-red-darken-1";
  if (days === 0) return "text-orange-darken-3";
  return "text-grey-darken-2";
};

// the endpoint returns every type in one unpaginated list, so the type filter is applied in place
const visibleItems = computed(() =>
  typeFilter.value === allTypes ? items.value : items.value.filter((row) => row.type === typeFilter.value),
);

const typeCounts = computed<ExpirationTabCounts>(() => {
  const counts: ExpirationTabCounts = {
    [allTypes]: items.value.length,
    [ExpirationType.licenceExpire]: 0,
    [ExpirationType.driverLicenseExpire]: 0,
    [ExpirationType.companyInsuranceExpire]: 0,
  };

  for (const row of items.value) {
    counts[row.type] = (counts[row.type] ?? 0) + 1;
  }

  return counts;
});

// the type column only adds noise once a single type is selected
const visibleHeaders = computed(() =>
  typeFilter.value === allTypes ? headers : headers.filter((header) => header.key !== "type"),
);

watch(typeFilter, () => {
  page.value = 1;
});

const dialogText = computed(() =>
  selectedRow.value ? t("trucks.confirm-truck-on-hold", { number: selectedRow.value.truckNumber }) : "",
);

async function refresh() {
  loading.value = true;
  searched.value = true;

  page.value = 1;

  const resp = await api.reports.getExpirations(filter.value.dateTo);
  if (resp.success && resp.data) {
    items.value = resp.data;
  } else {
    showAPIError(resp, t("trucks.expirations-load-failed"));
  }

  loading.value = false;
}

function clear() {
  filter.value = defaultFilter();
  typeFilter.value = allTypes;
  items.value = [];
  searched.value = false;
}

function confirmHold(row: ExpirationRowDto) {
  selectedRow.value = row;
  dialogVisible.value = true;
}

function closeDialog() {
  dialogVisible.value = false;
  selectedRow.value = null;
}

async function hold() {
  const row = selectedRow.value;
  if (!row) {
    return;
  }

  holding.value = true;

  const resp = await api.trucks.updateTruckStatus(row.truckId, { status: TruckStatus.OnHold });

  holding.value = false;

  if (!resp.success) {
    showAPIError(resp, t("trucks.set-on-hold-failed"));
    return;
  }

  // a truck on hold drops out of the report, so its rows leave the list without a reload
  items.value = items.value.filter((item) => item.truckId !== row.truckId);

  snackbarStore.showSnackbarSuccess(t("trucks.truck-on-hold-success", { number: row.truckNumber }));
  closeDialog();
}

onMounted(() => {
  setTitle(t("trucks.expirations"));
});
</script>

<template>
  <v-container :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100">
    <page-header :header="t('trucks.expirations')"></page-header>

    <table-filter :expandable="false" :filters="filter" @apply="refresh" @clear="clear">
      <template #header>
        <table-filter-row :divider="false" :label="t('general.filter-main')" icon="mdi-filter-variant">
          <v-col cols="12" sm="3">
            <date-picker v-model="filter.dateTo" :label="t('trucks.expiring-on-or-before')" @search="refresh" />
          </v-col>
        </table-filter-row>
      </template>
    </table-filter>

    <v-card v-if="!searched" class="rounded-0 pa-8 text-center text-grey-darken-1" color="white" variant="flat">
      {{ t("trucks.expirations-search-prompt") }}
    </v-card>

    <template v-else>
      <div class="d-flex align-center justify-space-between mb-4">
        <omni-button-toggle v-model="typeFilter" border="sm" divided mandatory variant="text">
          <v-btn v-for="option in typeOptions" :key="option.value" :value="option.value" size="small">
            {{ option.label }} ({{ typeCounts[option.value] }})
          </v-btn>
        </omni-button-toggle>
        <div class="text-grey-darken-1 font-weight-500">
          {{ t("general.results-found", { count: visibleItems.length }) }}
        </div>
      </div>

      <omni-table
        v-model:page="page"
        :headers="visibleHeaders"
        :items="visibleItems"
        :items-per-page="defaultPageSize"
        :loading="loading"
        :mobile-breakpoint="0"
        :server-data="false"
      >
        <template #[`item.truckNumber`]="{ item }">
          <router-link
            v-if="canEditTruck"
            :to="{ name: 'truck-edit', params: { id: item.truckId } }"
            class="text-decoration-none text-primary"
          >
            {{ item.truckNumber }}
          </router-link>
          <span v-else>{{ item.truckNumber }}</span>
        </template>
        <template #[`item.ownerName`]="{ item }">
          <router-link
            v-if="canEditUser"
            :to="{ name: 'user-edit', params: { id: item.ownerId } }"
            class="text-decoration-none text-primary"
          >
            {{ item.ownerName }}
          </router-link>
          <span v-else>{{ item.ownerName }}</span>
        </template>
        <template #[`item.ownerCompanyName`]="{ item }">
          {{ mdash(item.ownerCompanyName) }}
        </template>
        <template #[`item.driverName`]="{ item }">
          <router-link
            v-if="item.driverName && item.driverId && canEditUser"
            :to="{ name: 'user-edit', params: { id: item.driverId } }"
            class="text-decoration-none text-primary"
          >
            {{ item.driverName }}
          </router-link>
          <span v-else>{{ mdash(item.driverName) }}</span>
        </template>
        <template #[`item.type`]="{ item }">
          {{ expirationTypeLabels[item.type as ExpirationType] }}
        </template>
        <template #[`item.expireDate`]="{ item }">
          <div>{{ formatDate(item.expireDate) }}</div>
          <div :class="daysLeftClass(item.expireDate)" class="font-size-12">
            {{ daysLeftText(item.expireDate) }}
          </div>
        </template>
        <template #[`item.actions`]="{ item }">
          <omni-tooltip
            v-if="canChangeTruckStatus"
            :disabled="!isInRoute(item)"
            container-class="d-flex justify-center"
          >
            <template #activator>
              <v-btn
                :disabled="isOnHold(item) || isInRoute(item)"
                class="text-uppercase font-weight-600"
                color="primary"
                size="small"
                variant="outlined"
                @click="confirmHold(item)"
              >
                {{ t("trucks.set-truck-on-hold") }}
              </v-btn>
            </template>
            {{ t("trucks.truck-status-change-unavailable") }}
          </omni-tooltip>
        </template>
        <template #no-data>
          <div class="pa-4 text-grey-darken-1">{{ t("trucks.no-expirations") }}</div>
        </template>
      </omni-table>
    </template>

    <omni-dialog
      v-model="dialogVisible"
      :primary-action-label="t('general.confirm')"
      :primary-button-loading="holding"
      :secondary-action-label="t('general.cancel')"
      :size="DialogSize.small"
      :title="t('trucks.set-truck-on-hold')"
      persistent
      @primary-action:click="hold"
      @secondary-action:click="closeDialog"
      @on-close="closeDialog"
    >
      <div class="mt-4">{{ dialogText }}</div>
      <div class="mb-4 text-grey-darken-1">{{ t("trucks.truck-will-leave-list") }}</div>
    </omni-dialog>
  </v-container>
</template>

<style lang="scss" scoped></style>
